import React, { useEffect, useState } from "react";

import { IconRefreshCw } from "@frontend/common";

import { useRegistryAgency } from "@/contexts/RegistryAgencyContext";
import BorderBox from "@atoms/BorderBox/BorderBox";
import FilterTag from "@atoms/FilterTag/FilterTag";
import IconCheck from "@images/common/icon-filter-check-white.svg";
import XMarkIcon from "@images/pressList/icon-x-mark-red.svg";
import { useCtgListFilterState } from "@stores/ctgList";

import RegistryAgencyFilterSection from "./components/RegistryAgencyFilterSection/RegistryAgencyFilterSection";
import * as S from "./RegistryAgencyFilter.style";
import {
  StatusFilterList,
  StatusFilterValue,
} from "./RegistryAgencyFilter.types";

interface Props {
  closeModal: () => void;
}

const RegistryAgencyFilter = ({ closeModal }: Props) => {
  const [ctgListFilter, setCtgListFilter] = useCtgListFilterState();
  const { registryAgency } = ctgListFilter;
  const { RegistryAgencyData, isLoading } = useRegistryAgency();

  const [registryAgencyFilterList, setRegistryAgencyFilterList] =
    useState<StatusFilterList>({} as StatusFilterList);
  const [isAllChecked, setIsAllChecked] = useState(false);

  const handleReset = () => {
    if (RegistryAgencyData) {
      const resetList = Object.entries(RegistryAgencyData).reduce(
        (result, [key, value]) => {
          return {
            ...result,
            [key]: [{ code: key, name: value, isChecked: false }],
          };
        },
        {} as StatusFilterList,
      );
      setRegistryAgencyFilterList(resetList);
    }
  };

  const handleApplyRegistryAgency = () => {
    const updatedRegistryAgencyFilterList = Object.entries(
      registryAgencyFilterList,
    ).flatMap(([, value]) =>
      (value as StatusFilterValue[]).filter((item) => item.isChecked),
    );
    const newRegistryAgencyFilter = updatedRegistryAgencyFilterList.map(
      (item) => item.code,
    );
    setCtgListFilter({
      ...ctgListFilter,
      registryAgency: newRegistryAgencyFilter,
      page: 1,
    });
    closeModal();
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.currentTarget;

    const updatedRegistryAgencyFilterList = Object.fromEntries(
      Object.entries(registryAgencyFilterList).map(([key, value]) => [
        key,
        value.map((item) => ({ ...item, isChecked: checked })),
      ]),
    );

    setRegistryAgencyFilterList(updatedRegistryAgencyFilterList);
    setIsAllChecked(checked);
  };

  useEffect(() => {
    if (RegistryAgencyData && !isLoading) {
      const initRegistryAgencyFilterList: StatusFilterList = Object.entries(
        RegistryAgencyData,
      ).reduce((result, [key, value]) => {
        const updatedList = [
          {
            code: key,
            name: value,
            isChecked: registryAgency.includes(key),
          },
        ];
        return { ...result, [key]: updatedList };
      }, {} as StatusFilterList);
      setRegistryAgencyFilterList(initRegistryAgencyFilterList);
      setIsAllChecked(false);
    }
  }, [RegistryAgencyData, registryAgency, isLoading]);

  const totalCheckedRegistryAgency = Object.entries(registryAgencyFilterList)
    .map(
      ([, value]) =>
        (value as StatusFilterValue[]).filter((item) => item.isChecked).length,
    )
    .reduce((acc, cur) => acc + cur, 0);

  useEffect(() => {
    if (!registryAgencyFilterList) return;

    const totalItems = Object.values(registryAgencyFilterList).flat().length;
    const checkedItems = Object.values(registryAgencyFilterList)
      .flat()
      .filter((item) => item.isChecked).length;

    setIsAllChecked(totalItems > 0 && totalItems === checkedItems);
  }, [registryAgencyFilterList]);

  const confirmButtonText =
    totalCheckedRegistryAgency > 1
      ? "Apply Registry Agencies"
      : "Apply Registry Agency";

  if (isLoading) {
    return (
      <S.StatusFilterWrapper>
        <BorderBox padding="16">
          <div>Loading registry agencies...</div>
        </BorderBox>
      </S.StatusFilterWrapper>
    );
  }

  return (
    <S.StatusFilterWrapper>
      <BorderBox padding="16">
        <S.StatusFilterContentWrapper>
          <S.StatusFilterTitleRow>
            <S.FilterResetButton
              onClick={handleReset}
              disabled={totalCheckedRegistryAgency < 1}
            >
              {totalCheckedRegistryAgency < 1 ? (
                <IconRefreshCw width={20} height={20} color="#a5a5a5" />
              ) : (
                <IconRefreshCw width={20} height={20} color="#515C6C" />
              )}
              <S.FilterResetButtonText>Reset</S.FilterResetButtonText>
            </S.FilterResetButton>

            <S.ClassificationCloseButton
              type="button"
              onClick={() => closeModal()}
            >
              <S.ClassificationXMark>
                <img src={XMarkIcon} alt="close filter" />
              </S.ClassificationXMark>
              <S.ClassificationCloseText>Close</S.ClassificationCloseText>
            </S.ClassificationCloseButton>
          </S.StatusFilterTitleRow>

          <S.StatusFilterTitleWrapper>
            <S.StatusFilterTitle>Registry Agency</S.StatusFilterTitle>
            <FilterTag
              name="registryAgency"
              value="all"
              onChange={handleSelectAll}
              checked={isAllChecked}
              isAll
            >
              Select All
            </FilterTag>
          </S.StatusFilterTitleWrapper>
          <S.StatusFilterRow>
            {Object.entries(registryAgencyFilterList).map(([key, value]) => (
              <S.StatusFilterSectionWrapper
                key={`registry-agency-section-${key}`}
              >
                <RegistryAgencyFilterSection
                  statusSectionList={value as StatusFilterValue[]}
                  filterKey={key}
                  statusFilterList={registryAgencyFilterList}
                  setStatusFilterList={setRegistryAgencyFilterList}
                />
              </S.StatusFilterSectionWrapper>
            ))}
          </S.StatusFilterRow>
          <S.ConfirmButtonWrapper>
            <S.ConfirmButton onClick={handleApplyRegistryAgency}>
              <img src={IconCheck} alt="filter check icon" />
              <S.ConfirmButtonText>{confirmButtonText}</S.ConfirmButtonText>
            </S.ConfirmButton>
          </S.ConfirmButtonWrapper>
        </S.StatusFilterContentWrapper>
      </BorderBox>
    </S.StatusFilterWrapper>
  );
};

export default RegistryAgencyFilter;
